/**
 * 结果表格组件
 * 展示搜索结果的表格
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  FileText, 
  Trash2, 
  Eye,
  Copy
} from 'lucide-react';
import { clsx } from 'clsx';
import { formatTimestamp, formatRelativeTime, formatContentType, truncateText } from '../utils/format';
import Button from './ui/Button';
import Card from './ui/Card';
import Modal from './ui/Modal';
import type { SearchResult, SearchRecord } from '../types';

interface ResultsTableProps {
  results: SearchResult | null;
  loading?: boolean;
  onDelete?: (id: string) => void;
  onBatchDelete?: (key: string, value: string) => void;
  className?: string;
}

const ResultsTable: React.FC<ResultsTableProps> = ({
  results,
  loading = false,
  onDelete,
  onBatchDelete,
  className
}) => {
  const [selectedRecord, setSelectedRecord] = useState<SearchRecord | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // 查看详细信息
  const handleViewDetail = (record: SearchRecord) => {
    setSelectedRecord(record);
    setShowDetailModal(true);
  };

  // 复制到剪贴板
  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // 这里可以添加成功提示
    } catch (error) {
      console.error('复制失败:', error);
    }
  };

  // 批量删除
  const handleBatchDelete = (key: string, value: string) => {
    if (onBatchDelete) {
      onBatchDelete(key, value);
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <div className="animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-200 rounded"></div>
          ))}
        </div>
      </Card>
    );
  }

  if (!results || results.data.length === 0) {
    return (
      <Card className={className}>
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">暂无数据</h3>
          <p className="text-gray-500">请尝试调整搜索条件</p>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card className={className}>
        <div className="space-y-4">
          {/* 结果统计 */}
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              共找到 <span className="font-medium text-gray-900">{results.total}</span> 条记录
              {results.total > results.data.length && (
                <span>，显示前 {results.data.length} 条</span>
              )}
            </div>
          </div>

          {/* 表格 */}
          <div className="overflow-hidden">
            <div className="space-y-2">
              {results.data.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  {/* 主行 */}
                  <div className="bg-white hover:bg-gray-50 transition-colors">
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {formatContentType(record.content_type)}
                              </span>
                              <span className="text-xs text-gray-500 flex items-center">
                                <Calendar className="w-3 h-3 mr-1" />
                                {formatRelativeTime(record.received_at)}
                              </span>
                            </div>
                            <div className="text-sm text-gray-900">
                              {record.matched_pairs && record.matched_pairs.length > 0 ? (
                                <span>
                                  {record.matched_pairs.slice(0, 3).map((kv, i) => (
                                    <span key={i} className="mr-2">
                                      <span className="font-medium">{kv.key}:</span>
                                      <span className="text-gray-600 ml-1">
                                        {truncateText(kv.value, 30)}
                                      </span>
                                    </span>
                                  ))}
                                  {record.matched_pairs && record.matched_pairs.length > 3 && (
                                    <span className="text-gray-400">
                                      +{record.matched_pairs.length - 3} 更多
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-gray-500">无键值对数据</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetail(record)}
                            icon={<Eye />}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopy(record.raw_body)}
                            icon={<Copy />}
                          />
                          {onDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onDelete(record.id)}
                              icon={<Trash2 />}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* 详细信息模态框 */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="记录详细信息"
        size="xl"
      >
        {selectedRecord && (
          <div className="space-y-6">
            {/* 基本信息 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">记录ID:</span>
                <p className="text-gray-900 font-mono text-xs mt-1 break-all">{selectedRecord.id}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">内容类型:</span>
                <p className="text-gray-900 mt-1">{selectedRecord.content_type}</p>
              </div>
              <div>
                <span className="font-medium text-gray-700">接收时间:</span>
                <p className="text-gray-900 mt-1">{formatTimestamp(selectedRecord.received_at)}</p>
              </div>
            </div>

            {/* 键值对 */}
            {selectedRecord.matched_pairs && selectedRecord.matched_pairs.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-700 mb-3">键值对数据</h4>
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="max-h-80 overflow-y-auto">
                    {selectedRecord.matched_pairs.map((kv, index) => (
                      <div
                        key={index}
                        className={clsx(
                          'flex items-center justify-between p-3 text-sm',
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        )}
                      >
                        <div className="flex-1 min-w-0 mr-4">
                          <div className="font-medium text-gray-900 mb-1">
                            {kv.key}
                          </div>
                          <div className="text-gray-600 break-all">
                            {kv.value}
                          </div>
                        </div>
                        {onBatchDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleBatchDelete(kv.key, kv.value)}
                            icon={<Trash2 />}
                            className="flex-shrink-0"
                          >
                            批量删除
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* 原始数据 */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-700">原始数据</h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopy(selectedRecord.raw_body)}
                  icon={<Copy />}
                >
                  复制原始数据
                </Button>
              </div>
              <div className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm font-mono overflow-auto max-h-96">
                <pre>{selectedRecord.raw_body}</pre>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ResultsTable;